import React, {useState, useEffect} from 'react';
import propTypes from 'prop-types';
import styled from 'styled-components';
import {Grid, Box} from '@material-ui/core';
import {theme} from '../../../themes/default-theme';
import Text from '../../../components/typography';
import Cta from '../../../components/ui/Cta';
import {Links} from '../../../components/ui';
import NextLink from '../../../components/navigation/NextLink';
import Checkbox from '../../../components/forms/Checkbox';
import {useMediaQuery} from '@material-ui/core';
import SeriesCard from '../../../components/navigation/NewMainNav/SeriesCard';

import Equalizer from 'react-equalizer';


const Wrapper = styled.div`
  width:100%;
  .grid-item {
    ${theme.breakpoints.up('lg')} { 
      padding-top:0;
      padding-bottom:0;
      &:first-child {
        padding-left:0;
      }
      &:last-child {
        padding-right:0;
      }
    }
    ${theme.breakpoints.down('md')} { 
      width: 100%;
      padding-left:0;
      padding-right:0;
      &:first-child {
        padding-top:0;
      }
      &:last-child {
        padding-bottom:0;
      }
    }
  }
`

const Intro = styled.div`
  text-align: left;

  h3 {
    font-weight:800;
    margin-bottom:10px;
  }
  p {
    &:last-child {
      margin-bottom:0;
      padding-bottom:0;
    }
  }
  ${theme.breakpoints.down('sm')} { 
    text-align:center;
    max-width:100%;
    p, div {
      text-align:center;
    }
    h3 {
      text-align:center;
      background:${theme.palette.white};
      padding:8px;
      width:100%;
    }
  }
  a {
    font-size:13px;
    color:${theme.palette.gunmentalGrey};
    text-decoration:none;
    font-weight:700;
    margin-top:8px;
    display:inline-block;
    ${theme.breakpoints.up('md')} { 
      font-size:16px;
    }
    &:hover {
      color:${theme.palette.vividBlue};
    }
  }
`
const SeriesCardContainer = styled(Equalizer)`  
  width:100%;
  text-align:center;
  display:flex;
  align-items:flex-start;
  justify-content:flex-start;
  flex-direction:row;
  flex-wrap:wrap;
  gap:10px;
  ${theme.breakpoints.down('sm')} {
    gap:20px;
  }
`

const SeriesCardItem = styled.div`
  width:49%;
  display: flex;

  ${props => props.spaceBetween ? `
    justify-content: space-between;
  ` : `
    justify-content: flex-start;
  `}

  flex-direction: column;
  
  ${theme.breakpoints.up('lg')} { 
    width:32%;
  }
  ${theme.breakpoints.down('sm')} {
     width:46%;
    a {
      display:inline-block;
      width:auto;
    }
  }


  ${theme.breakpoints.down('md')} {
    > div {
      margin: 0 auto;
    }
  }

  div {
    padding:0;
    justify-content:center;
  }
  h3 {
    text-align:center;
  }
  label {
    margin-right: 0;
  }
  picture, img {
    width: 275px;
    ${theme.breakpoints.down('sm')} {
      width:100%;
      max-width:275px;
    }
  }
`

const CompareContainer = styled.div`
  width:100%;
  text-align:center;
  margin-top:-10px;
  margin-bottom:10px;
  max-width:275px;
  ${theme.breakpoints.down('sm')} {
    margin-bottom:0;
    margin-top:0;
    max-width:100%;
  }

`
const ModelContainer = styled.div`
  margin-top: 20px;
`
const Model = styled.div`
  text-align: center;
  margin-bottom: 5px;

  > span,
  > a {
    display: inline-block;
    border: 1px solid ${theme.palette.middleGrey};
    color: ${theme.palette.gunmentalGrey};
    padding: 2px 14px;
    border-radius: 25px;
    text-decoration: none;

    a {
      text-decoration: none;
      color: ${theme.palette.gunmentalGrey};
    }
  }
`

const RangeCategorySection = (props) => {
  const {     
    title,
    text,
    learnMore,
    series,
    showModelsInThumbnail,
    onCompare
  } = props

  const desktopView = useMediaQuery(theme.breakpoints.up('md'));
  const mobileView = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Wrapper>
      <Grid container spacing={desktopView ? 5 : 3} direction={desktopView ? 'row' : 'column'} wrap="wrap" justify={'space-between'} align={'flex-start'}> 
        <Grid item xs={12} lg={3} className="grid-item">
          <Intro>
            {title && 
              <Text variant="h3" colour="vividBlue" uppercase="true" align="left">{title}</Text>
            }
            {text && 
              <Text variant="body1" align="left">{text}</Text>
            }
            {learnMore && title &&
              <NextLink href={learnMore.href}>
                <a>View all {title}</a>
              </NextLink>
            }
          </Intro>
        </Grid>
        <Grid item xs={12} lg={9} className="grid-item">
            <SeriesCardContainer enabled={() => series.length > 1 ? true : false}>
              {series && series.map((item, index) => {
                return (
                  <SeriesCardItem key={item.title} spaceBetween={showModelsInThumbnail ? false : true}>
                      <SeriesCard 
                        href={item.href}
                        title={item.title}
                        subTitle={item.subTitle}
                        sleeps={item.sleeps}
                        image={item.image}
                        hasborder='false'
                        hideSleeps={true}
                      />
                      <CompareContainer>
                      {showModelsInThumbnail ? 
                        <ModelContainer>
                          {item.models.map(item => {
                            return <Model key={item.floorplan}>
                                <NextLink href={item.url}>
                                  <a>{item.floorplan}</a>
                                </NextLink>
                              </Model>
                          })}
                        </ModelContainer>
                      :
                        <Checkbox
                          label="Compare"
                          name={item.title}
                          onChange={(event) => onCompare(item.compareValues, event.target.checked)}
                        />
                      }
                      </CompareContainer>
                  </SeriesCardItem>
                )
              })}
            </SeriesCardContainer>
        </Grid>
      </Grid>
    </Wrapper>
  )
}
  
RangeCategorySection.propTypes = {
  id: propTypes.string,
  title: propTypes.string,
  text: propTypes.string,
  learnMore: propTypes.string,
  series: propTypes.array,
  showModelsInThumbnail: propTypes.bool,
  onCompare: propTypes.func
}
  
RangeCategorySection.defaultProps = {
  id: null,
  href: null,
  title: null,
  subTitle: null,
  sleep: null,
  image: null,
  showModelsInThumbnail: false,
  onCompare: () => {}
}
  
export default RangeCategorySection